import { forwardRef } from 'react';

import { Link, useNavigate, useParams } from 'react-router-dom';

import { DocWithFacts } from '../../../../../../../../api/types/queries';
import { cn } from '@/helpers/shadCnUtils';
import { Icon } from '@/new/components/icon';
import { ProcessingChip, getProcessingState, ProcessingState } from '@/new/components/processing-chip';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/new/components/shadcn/tooltip';

export const DocRow = forwardRef<
  HTMLDivElement,
  {
    doc: DocWithFacts;
    subsetId: string;
    searchQuery?: string;
    isSelected?: boolean;
    onSelect?: () => void;
    // Document selection for chronologies
    isDocSelected?: boolean;
    onDocSelectionChange?: (docId: string, selected: boolean) => void;
  }
>(({ doc, subsetId, searchQuery, isSelected = false, onSelect, isDocSelected = false, onDocSelectionChange }, ref) => {
  const { matterId } = useParams();
  const navigate = useNavigate();

  const processingState = getProcessingState(doc);

  return (
    <div
      ref={ref}
      className={cn(
        'group relative cursor-pointer scroll-my-8 rounded-lg border border-gray-300 bg-white transition-all duration-200 hover:shadow-lg/7 hover:shadow-teal-500',

        isSelected
          ? processingState === ProcessingState.NotProcessed
            ? 'border-red-400'
            : 'border-teal-400'
          : processingState === ProcessingState.NotProcessed
            ? 'border-red-200'
            : 'border-gray-300',
      )}
      onClick={onSelect}
    >
      <div
        className={cn(
          'flex items-center justify-between rounded-t-lg border-b p-4 py-1.75 pr-1.5',
          processingState === ProcessingState.NotProcessed ? 'border-red-200 bg-red-50' : 'bg-gray-50',
        )}
      >
        <span className="flex items-center gap-2">
          {processingState === ProcessingState.NotProcessed ? (
            <Icon className={cn('lucide--file shrink-0 text-xs text-red-400 group-hover:text-red-500')} />
          ) : (
            <Checkbox
              className="bg-white"
              checked={isDocSelected}
              onClick={(e) => e.stopPropagation()}
              onCheckedChange={(checked) => {
                onDocSelectionChange?.(doc.id, checked === true);
              }}
            />
          )}

          <h3 className="line-clamp-1 text-xs font-medium text-ellipsis text-gray-900/80" title={doc.file.name}>
            {doc.file.name}
          </h3>
          {doc.file.fileMetadata?.relativityFields &&
            Object.keys(doc.file.fileMetadata.relativityFields).length > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center rounded bg-purple-100 px-1.5 py-0.5">
                      <Icon className="lucide--database h-3 w-3 text-purple-600" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="text-xs">Relativity Metadata Available</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          {searchQuery && doc.chunkMatchCount > 0 && (
            <Link
              to={`/matters/${matterId}/subsets/${subsetId}/docs/${doc.id}?searchQuery=${encodeURIComponent(searchQuery)}`}
              className="mr-1.5 flex cursor-pointer items-center rounded bg-teal-100 px-1.5 py-0.5 transition-colors hover:bg-teal-200"
            >
              <span className="text-[0.65rem] font-medium whitespace-nowrap text-teal-700">
                {doc.chunkMatchCount} {doc.chunkMatchCount === 1 ? 'match' : 'matches'} in content
              </span>
            </Link>
          )}
        </span>
        <div className="flex items-center gap-2">
          {processingState !== ProcessingState.Processed && (
            <ProcessingChip processingState={getProcessingState(doc)} />
          )}
          <Button size="sm" asChild>
            <Link to={`/matters/${matterId}/subsets/${subsetId}/docs/${doc.id}`}>
              View
              <Icon className="iconify lucide--arrow-right" />
            </Link>
          </Button>
        </div>
      </div>

      <div className="p-4 py-2 pb-2.5 text-xs">
        <div className="line-clamp-1">
          <span className="pr-1 font-medium text-gray-800/60">Author:</span>
          {doc.author ? (
            <span className="text-gray-900/80" title={doc.author}>
              {doc.author}
            </span>
          ) : (
            <span className="font-medium text-gray-500">N/A</span>
          )}
        </div>

        <div className="line-clamp-1">
          <span className="pr-1 font-medium text-gray-800/60">Date:</span>
          {doc.dateText ? (
            <span className="text-gray-900/80">{doc.dateText}</span>
          ) : (
            <span className="font-medium text-gray-500">N/A</span>
          )}
        </div>
      </div>
    </div>
  );
});

DocRow.displayName = 'DocRow';
